import { Editor } from '@tiptap/core';

import {
  ComposedMessage,
  PromptChipAttrs,
  PromptUse,
} from './SlashExtension.types';

// Private-use marks stand in for chips while the document is read as text.
const OPEN = '';
const CLOSE = '';
const CHIP_MARK = /(\d+)/;

/**
 * The message the agent receives.
 *
 * With a Prompt Template chip (switch on): chip bodies and free text in
 * document order, each trimmed and joined with `\n`, plus the `prompts`
 * record for chips with a template. Every range lies inside the text.
 *
 * Without one (switch off, or no chips): exactly today's `editor.getText()`,
 * with any string chip inline as its text.
 */
export function composeMessage(editor: Editor): ComposedMessage {
  const chipsByPos = new Map<number, PromptChipAttrs>();
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'promptChip') {
      chipsByPos.set(pos, node.attrs as PromptChipAttrs);
    }
  });

  const hasTemplate = [...chipsByPos.values()].some(
    chip => chip.templateId != null
  );
  if (!hasTemplate) {
    return {
      text: editor.getText({
        textSerializers: {
          promptChip: ({ node }) => (node.attrs as PromptChipAttrs).body,
        },
      }),
      prompts: [],
    };
  }

  const raw = editor.getText({
    textSerializers: {
      promptChip: ({ pos }) => `${OPEN}${pos}${CLOSE}`,
    },
  });

  // split with a capture group: free text at even indexes, chip positions at odd.
  const parts = raw.split(CHIP_MARK);
  const prompts: PromptUse[] = [];
  let text = '';
  let segments = 0;

  const append = (segment: string) => {
    if (segments > 0) text += '\n';
    segments += 1;
    const start = text.length;
    text += segment;
    return start;
  };

  parts.forEach((part, index) => {
    if (index % 2 === 0) {
      const freeText = part.trim();
      if (freeText) append(freeText);
      return;
    }

    const chip = chipsByPos.get(Number(part));
    // Trimmed like the free text, so the sent (trimmed) message keeps the range.
    const body = chip?.body.trim();
    if (!chip || !body) return;
    const start = append(body);

    if (chip.templateId != null) {
      prompts.push({
        templateId: chip.templateId,
        version: chip.version ?? 0,
        title: chip.title,
        at: [start, start + body.length],
        values: { ...(chip.values ?? {}) },
        edited: !!chip.edited,
      });
    }
  });

  return { text, prompts };
}

/**
 * A composer send trimmed for the wire, with its Prompt Templates record moved
 * to match: every send path (Connie, Tia, Salma, the document and quote chats)
 * uses this one helper. The record's ranges are in the untrimmed `message`;
 * trimming shifts them by the leading whitespace, and each range is clamped to
 * the sent text. `prompts` is empty when the send had no chips.
 */
export const trimPromptedMessage = (
  message: string,
  prompts?: PromptUse[]
): { text: string; prompts: PromptUse[] } => {
  const text = message.trim();
  const shift = message.length - message.trimStart().length;
  const clamp = (index: number) => Math.min(Math.max(index, 0), text.length);
  return {
    text,
    prompts: (prompts ?? []).map(prompt => {
      const start = clamp(prompt.at[0] - shift);
      const end = Math.max(start, clamp(prompt.at[1] - shift));
      return { ...prompt, at: [start, end] as [number, number] };
    }),
  };
};
